/**
 * AIEmailHelper Component - AI assistant for drafting emails
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Sparkles, Loader2, Copy, Check, Wand2 } from 'lucide-react';
import { useAIGeneration, getErrorMessage } from './utils';

export const AIEmailHelper = ({ recipientName, onInsert }) => {
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState('');
  const [subject, setSubject] = useState('');
  const [tone, setTone] = useState('professional');
  
  const { loading, generatedText, setGeneratedText, copied, generate, handleCopy } = useAIGeneration('draft-email');
  
  const handleGenerate = async () => {
    if (!context.trim()) {
      toast.error('Please describe what the email should be about');
      return;
    }
    
    try {
      const response = await generate({
        recipient_name: recipientName || 'Member',
        subject: subject || null,
        context: context,
        tone: tone
      });
      setGeneratedText(response.generated_text);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate email'));
    }
  };
  
  const handleInsert = () => {
    if (onInsert) onInsert(generatedText);
    setOpen(false);
    setGeneratedText('');
    setContext('');
    toast.success('Text inserted');
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          AI Assist
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-500" />
            AI Email Assistant
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>What should this email be about?</Label>
            <Textarea
              placeholder="E.g., Welcome them to the community and explain how to get started with Module 1..."
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={3}
              className="mt-1"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Subject Line (optional)</Label>
              <Input
                placeholder="Suggested subject..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional & Warm</SelectItem>
                  <SelectItem value="friendly">Friendly & Casual</SelectItem>
                  <SelectItem value="formal">Formal</SelectItem>
                  <SelectItem value="encouraging">Encouraging & Supportive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Button 
            onClick={handleGenerate} 
            disabled={loading || !context.trim()}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generate Email</>
            )}
          </Button>
          
          {generatedText && (
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm text-gray-600">Generated Email:</Label>
                  <Button variant="ghost" size="sm" onClick={() => handleCopy(generatedText)}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <div className="whitespace-pre-wrap text-sm bg-white p-3 rounded border max-h-60 overflow-y-auto">
                  {generatedText}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        {generatedText && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGeneratedText('')}>
              Regenerate
            </Button>
            <Button onClick={handleInsert} className="bg-cyan-600 hover:bg-cyan-700">
              Use This Text
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
